import {
  FirstRunOptions,
  type FirstRunOption,
  type FirstRunOptionId,
} from '@/containers/onboarding/first-run/first-run-options.component';
import { useFeatureAccess } from '@/hooks/use-feature-access.hook';
import { useAppDispatch } from '@/store';
import { sendAiChatMessage } from '@/store/features/ui.feature';
import { useUser } from '@clerk/react';
import { useRef } from 'react';

/**
 * The `first_run_options` display: the four ways to start under Clu's first
 * answer, each sending its prompt into the chat, and the setup call with the
 * teammate the API routed it to. A Free workspace gets its own four, with no
 * sequence or agent.
 */

const OPTION_IDS: ReadonlySet<string> = new Set([
  'tour',
  'find',
  'sequence',
  'agent',
  'companies',
  'enrich',
]);

/** The tiles a workspace that can't build sequences and agents can't use. */
const PAID_ONLY: ReadonlySet<string> = new Set(['sequence', 'agent']);

/** The setup-call hosts the API routes to. */
const HOSTS: Record<string, { name: string; photo: string }> = {
  lev: { name: 'Lev', photo: '/team/lev.jpg' },
  sameer: { name: 'Sameer', photo: '/team/sameer.jpg' },
};

interface ParsedOption extends FirstRunOption {
  prompt: string;
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object'
    ? (value as Record<string, unknown>)
    : {};
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function parseOptions(value: unknown): ParsedOption[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    const option = record(item);
    const id = text(option.id);
    const title = text(option.title);
    const prompt = text(option.prompt) || title;
    if (!OPTION_IDS.has(id) || !title) return [];
    return [
      {
        id: id as FirstRunOptionId,
        title,
        description: text(option.description),
        prompt,
      },
    ];
  });
}

export function FirstRunOptionsCard({
  result,
  display,
}: {
  result: unknown;
  display?: unknown;
}) {
  const dispatch = useAppDispatch();
  const { user } = useUser();
  const sentRef = useRef(false);
  // The payload rides on the result; older runs kept it on the display.
  const payload = Array.isArray(record(result).options)
    ? record(result)
    : record(display);
  // Free can't build sequences or agents: its own four (the API writes both
  // sets, since the plan is chosen after the run), or, from an older run,
  // the four less those two. Unknown access reads as Free.
  const { actions } = useFeatureAccess();
  const full =
    actions?.agents_build === true && actions?.sequences_build === true;
  const freeOptions = parseOptions(payload.free_options);
  const options = full
    ? parseOptions(payload.options)
    : freeOptions.length > 0
      ? freeOptions
      : parseOptions(payload.options).filter((o) => !PAID_ONLY.has(o.id));
  const call = record(payload.call);
  const host = HOSTS[text(call.host)];
  const bookingUrl = text(call.booking_url);
  const credits = typeof call.credits === 'number' ? call.credits : null;

  if (options.length === 0) return null;

  return (
    <FirstRunOptions
      options={options}
      onSelect={(id) => {
        const option = options.find((o) => o.id === id);
        if (!option || sentRef.current) return;
        sentRef.current = true;
        dispatch(sendAiChatMessage(option.prompt));
      }}
      call={
        host && bookingUrl && credits !== null
          ? {
              host: host.name,
              photo: host.photo,
              credits,
              onBook: () => {
                let url: URL;
                try {
                  url = new URL(bookingUrl);
                } catch {
                  window.open(bookingUrl, '_blank', 'noopener,noreferrer');
                  return;
                }
                const prefill: [string, string | null | undefined][] = [
                  ['name', user?.fullName],
                  ['email', user?.primaryEmailAddress?.emailAddress],
                ];
                for (const [key, value] of prefill) {
                  if (!value || url.searchParams.has(key)) continue;
                  // Match the booking step's percent encoding: Calendly
                  // displays URLSearchParams' space-as-plus encoding literally.
                  url.search += `${url.search ? '&' : '?'}${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
                }
                window.open(url.toString(), '_blank', 'noopener,noreferrer');
              },
            }
          : null
      }
    />
  );
}
